import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";

function useBlogSEO() {
  useEffect(() => {
    document.title = "Blog | Maltora — Beverage Operations Insights";
    const setMeta = (name: string, val: string, prop = "name") => {
      let el = document.querySelector(`meta[${prop}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(prop, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    setMeta(
      "description",
      "Practical guides on orders, inventory, credit, and distribution for beverage businesses."
    );
    setMeta(
      "keywords",
      "beverage distribution, brewery operations, inventory management, credit control, order management, Maltora"
    );
    setMeta("og:title", "Blog | Maltora", "property");
    setMeta(
      "og:description",
      "Practical guides on beverage operations, distribution, and finance.",
      "property"
    );
    setMeta("og:type", "website", "property");
    setMeta("og:url", "https://maltora.app/blog", "property");
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", "Maltora Blog — Beverage Operations");
    setMeta(
      "twitter:description",
      "Practical guides on orders, inventory, credit, and distribution."
    );
    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://maltora.app/blog";
    return () => {
      document.title = "Maltora — Beverage Operations Platform";
    };
  }, []);
}

const POSTS = [
  {
    slug: "stockouts-craft-beverage",
    category: "inventory",
    tag: "Inventory",
    tagColor: "#1F5F5B",
    tagBg: "rgba(31,95,91,0.1)",
    date: "2026-03-12",
    dateLabel: "Mar 12, 2026",
    readTime: "6 min read",
    title: "The real cost of stockouts in craft beverage distribution",
    excerpt:
      "A single stockout cascades into lost retailer trust, missed seasonals, and months of recovery. We break down the hidden math.",
    authorInitials: "MT",
    authorName: "Maltora Team",
    authorColor: "#1F5F5B",
  },
  {
    slug: "managing-credit",
    category: "finance",
    tag: "Finance",
    tagColor: "#b84d00",
    tagBg: "rgba(232,106,13,0.1)",
    date: "2026-03-05",
    dateLabel: "Mar 5, 2026",
    readTime: "5 min read",
    title:
      "How to set credit limits that protect cash flow without losing customers",
    excerpt:
      "Setting the right credit limits for retail partners is part science, part gut feeling. Here's how to do it systematically.",
    authorInitials: "MT",
    authorName: "Maltora Team",
    authorColor: "#b84d00",
  },
  {
    slug: "route-density",
    category: "distribution",
    tag: "Distribution",
    tagColor: "#334155",
    tagBg: "rgba(51,65,85,0.08)",
    date: "2026-02-26",
    dateLabel: "Feb 26, 2026",
    readTime: "7 min read",
    title:
      "Route density vs. frequency: how top distributors think about van planning",
    excerpt:
      "More stops or more visits? We analysed data from 40 distributors to find which approach actually wins.",
    authorInitials: "MT",
    authorName: "Maltora Team",
    authorColor: "#334155",
  },
  {
    slug: "order-engine",
    category: "operations",
    tag: "Operations",
    tagColor: "#1F5F5B",
    tagBg: "rgba(31,95,91,0.1)",
    date: "2026-02-18",
    dateLabel: "Feb 18, 2026",
    readTime: "4 min read",
    title:
      "What 500 orders a day taught us about building Maltora's order engine",
    excerpt:
      "From the product team: the decisions that shaped how Maltora processes, routes, and confirms orders in real time.",
    authorInitials: "MT",
    authorName: "Maltora Team",
    authorColor: "#1F5F5B",
  },
  {
    slug: "batch-tracking",
    category: "inventory",
    tag: "Inventory",
    tagColor: "#1F5F5B",
    tagBg: "rgba(31,95,91,0.1)",
    date: "2026-02-10",
    dateLabel: "Feb 10, 2026",
    readTime: "9 min read",
    title:
      "Batch tracking for craft beverage: compliance, freshness, and recalls done right",
    excerpt:
      "Batch tracking isn't optional — it's your safety net. Here's how to implement it without slowing down operations.",
    authorInitials: "MT",
    authorName: "Maltora Team",
    authorColor: "#1F5F5B",
  },
  {
    slug: "changelog-jan",
    category: "product",
    tag: "Product Update",
    tagColor: "#b84d00",
    tagBg: "rgba(232,106,13,0.1)",
    date: "2026-01-30",
    dateLabel: "Jan 30, 2026",
    readTime: "3 min read",
    title:
      "Maltora changelog: predictive restocking, multi-currency, and the new sales rep app",
    excerpt:
      "Everything we shipped in January — the features our Growth customers asked for most this quarter.",
    authorInitials: "MT",
    authorName: "Maltora Team",
    authorColor: "#b84d00",
  },
];

const FILTERS = [
  { key: "all", label: "All Posts" },
  { key: "operations", label: "Operations" },
  { key: "inventory", label: "Inventory" },
  { key: "finance", label: "Finance & Credit" },
  { key: "distribution", label: "Distribution" },
  { key: "product", label: "Product Updates" },
];

type Post = (typeof POSTS)[0];

function PostCard({ post }: { post: Post }) {
  return (
    <article
      itemScope
      itemType="https://schema.org/BlogPosting"
      style={{
        background: "#fff",
        borderRadius: "14px",
        border: "1px solid rgba(15,23,42,0.09)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s,transform 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 8px 28px rgba(15,23,42,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <div
        style={{
          height: "6px",
          background: `linear-gradient(90deg,${post.tagColor},${post.tagColor}88)`,
        }}
      />
      <div
        style={{
          padding: "22px 22px 20px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <span
            itemProp="articleSection"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 10px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: "800",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              background: post.tagBg,
              color: post.tagColor,
            }}
          >
            {post.tag}
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <time dateTime={post.date} itemProp="datePublished">
              {post.dateLabel}
            </time>
            <span aria-hidden="true">·</span>
            <span>{post.readTime}</span>
          </span>
        </div>
        <h2
          itemProp="headline"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "17px",
            fontWeight: "700",
            lineHeight: "1.35",
            letterSpacing: "-0.01em",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          <Link
            to={`/blog/${post.slug}`}
            state={{ fromApp: true }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {post.title}
          </Link>
        </h2>
        <p
          itemProp="description"
          style={{
            fontSize: "13.5px",
            color: "#64748b",
            lineHeight: "1.65",
            margin: 0,
            flex: 1,
          }}
        >
          {post.excerpt}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: "14px",
            borderTop: "1px solid rgba(15,23,42,0.07)",
          }}
        >
          <div
            itemProp="author"
            itemScope
            itemType="https://schema.org/Person"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <div
              aria-hidden="true"
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                background: `linear-gradient(135deg,${post.authorColor},${post.authorColor}99)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: "800",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              {post.authorInitials}
            </div>
            <span
              itemProp="name"
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "var(--text-primary)",
              }}
            >
              {post.authorName}
            </span>
          </div>
          <Link
            state={{ fromApp: true }}
            to={`/blog/${post.slug}`}
            aria-label={`Read: ${post.title}`}
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "var(--teal)",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              letterSpacing: "0.02em",
            }}
          >
            Read →
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function BlogPage() {
  useBlogSEO();
  const [activeFilter, setActiveFilter] = useState("all");
  const filtered =
    activeFilter === "all"
      ? POSTS
      : POSTS.filter((p) => p.category === activeFilter);

  return (
    <Layout>
      {/* HERO */}
      <section
        style={{
          padding: "56px 0 64px",
          background: "linear-gradient(135deg,var(--teal) 0%,#0F2D2B 100%)",
          position: "relative",
          overflow: "hidden",
        }}
        aria-labelledby="blog-hero-title"
      >
        <div className="container">
          <div style={{ maxWidth: "620px" }}>
            <p
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "11px",
                fontWeight: "800",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--orange)",
                marginBottom: "16px",
              }}
            >
              THE MALTORA BLOG
            </p>
            <h1
              id="blog-hero-title"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(30px,4vw,48px)",
                fontWeight: "700",
                color: "#fff",
                lineHeight: "1.12",
                letterSpacing: "-0.025em",
                marginBottom: "20px",
              }}
            >
              Straight talk for{" "}
              <em
                style={{
                  fontStyle: "italic",
                  color: "var(--orange)",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  lineHeight: "inherit",
                  fontFamily: "inherit",
                }}
              >
                beverage businesses.
              </em>
            </h1>
            <p
              style={{
                fontSize: "16px",
                color: "rgba(255,255,255,0.8)",
                lineHeight: "1.7",
                maxWidth: "480px",
                margin: 0,
              }}
            >
              Operations, inventory, distribution, and credit — practical guides
              written for the people running the supply chain, not reading about
              it.
            </p>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <nav
        aria-label="Filter posts by category"
        style={{
          padding: "22px 0",
          background: "#f8f6f2",
          borderBottom: "1px solid rgba(15,23,42,0.07)",
          position: "sticky",
          top: "64px",
          zIndex: 10,
        }}
      >
        <div className="container">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                aria-pressed={activeFilter === f.key}
                style={{
                  padding: "7px 16px",
                  borderRadius: "100px",
                  border: `1.5px solid ${
                    activeFilter === f.key
                      ? "var(--navy)"
                      : "rgba(15,23,42,0.14)"
                  }`,
                  background:
                    activeFilter === f.key ? "var(--navy)" : "transparent",
                  color: activeFilter === f.key ? "#fff" : "#475569",
                  fontFamily: "var(--font-ui)",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* POSTS + SIDEBAR */}
      <section
        aria-label="Blog posts"
        style={{ background: "#f8f6f2", padding: "56px 0 72px" }}
      >
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 296px",
              gap: "40px",
              alignItems: "start",
            }}
          >
            <div>
              <header
                className="section-reveal"
                style={{ marginBottom: "28px" }}
              >
                <p
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "11px",
                    fontWeight: "800",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--orange)",
                    marginBottom: "6px",
                  }}
                >
                  LATEST ARTICLES
                </p>
                <h2
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "clamp(22px,2.5vw,28px)",
                    fontWeight: "700",
                    letterSpacing: "-0.02em",
                    color: "var(--text-primary)",
                    margin: 0,
                  }}
                >
                  {activeFilter === "all"
                    ? "Recent posts."
                    : `${FILTERS.find((f) => f.key === activeFilter)?.label}.`}
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#64748b",
                      letterSpacing: "0",
                    }}
                  >
                    {filtered.length}{" "}
                    {filtered.length === 1 ? "article" : "articles"}
                  </span>
                </h2>
              </header>
              <div
                className="section-reveal"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
                  gap: "20px",
                }}
              >
                {filtered.map((post) => (
                  <PostCard key={post.slug} post={post} />
                ))}
                {filtered.length === 0 && (
                  <p style={{ color: "#64748b", padding: "32px 0" }}>
                    No posts in this category yet — check back soon.
                  </p>
                )}
              </div>
            </div>

            <aside
              aria-label="Blog sidebar"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                position: "sticky",
                top: "120px",
              }}
            >
              <div
                className="section-reveal"
                style={{
                  background: "#fff",
                  borderRadius: "14px",
                  border: "1px solid rgba(15,23,42,0.09)",
                  padding: "24px",
                }}
              >
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "16px",
                    fontWeight: "700",
                    margin: "0 0 16px",
                    color: "var(--text-primary)",
                  }}
                >
                  Most Read
                </h3>
                {[
                  {
                    num: "01",
                    title: "Why WhatsApp orders cost more than you think",
                    meta: "8 min · Operations",
                  },
                  {
                    num: "02",
                    title:
                      "Credit limits that protect without alienating buyers",
                    meta: "5 min · Finance",
                  },
                  {
                    num: "03",
                    title: "Batch tracking done right: compliance & freshness",
                    meta: "9 min · Inventory",
                  },
                  {
                    num: "04",
                    title:
                      "Route density vs. frequency: van planning decisions",
                    meta: "7 min · Distribution",
                  },
                ].map((item) => (
                  <div
                    key={item.num}
                    style={{
                      display: "flex",
                      gap: "12px",
                      padding: "11px 0",
                      borderBottom: "1px solid rgba(15,23,42,0.06)",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "18px",
                        fontWeight: "900",
                        color: "rgba(15,23,42,0.1)",
                        lineHeight: "1",
                        width: "22px",
                        flexShrink: 0,
                      }}
                    >
                      {item.num}
                    </span>
                    <div>
                      <p
                        style={{
                          fontSize: "12.5px",
                          fontWeight: "600",
                          lineHeight: "1.4",
                          margin: "0 0 3px",
                          color: "var(--text-primary)",
                        }}
                      >
                        {item.title}
                      </p>
                      <p
                        style={{
                          fontSize: "11.5px",
                          color: "#64748b",
                          margin: 0,
                        }}
                      >
                        {item.meta}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="section-reveal"
                style={{
                  background: "#fff",
                  borderRadius: "14px",
                  border: "1px solid rgba(15,23,42,0.09)",
                  padding: "24px",
                }}
              >
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "16px",
                    fontWeight: "700",
                    margin: "0 0 14px",
                    color: "var(--text-primary)",
                  }}
                >
                  Browse by Topic
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "7px",
                  }}
                >
                  {[
                    "Operations",
                    "Finance",
                    "Inventory",
                    "Distribution",
                    "Credit",
                    "Growth",
                    "Supply Chain",
                    "Compliance",
                  ].map((t) => (
                    <span
                      key={t}
                      style={{
                        display: "inline-flex",
                        padding: "4px 11px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "700",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        background: "rgba(31,95,91,0.08)",
                        color: "var(--teal)",
                        cursor: "pointer",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* TOPICS GRID */}
      <section
        aria-labelledby="topics-title"
        style={{ padding: "72px 0", background: "#fff" }}
      >
        <div className="container section-reveal">
          <header style={{ marginBottom: "40px" }}>
            <p
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "11px",
                fontWeight: "800",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--orange)",
                marginBottom: "8px",
              }}
            >
              BROWSE BY TOPIC
            </p>
            <h2
              id="topics-title"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(22px,2.8vw,32px)",
                fontWeight: "700",
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                margin: "0 0 10px",
              }}
            >
              Everything we write about.
            </h2>
            <p
              style={{
                color: "#64748b",
                fontSize: "15px",
                lineHeight: "1.65",
                maxWidth: "500px",
                margin: 0,
              }}
            >
              Deep guides on every part of running a beverage business — from
              the warehouse floor to the finance dashboard.
            </p>
          </header>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: "16px",
            }}
          >
            {[
              {
                icon: (
                  <>
                    <path d="M9 11l3 3L22 4" />
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                  </>
                ),
                color: "rgba(31,95,91,0.1)",
                stroke: "#1F5F5B",
                name: "Order Management",
                count: "14 articles",
                desc: "Digital orders, confirmations, modifications",
              },
              {
                icon: (
                  <>
                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                  </>
                ),
                color: "rgba(232,106,13,0.1)",
                stroke: "#b84d00",
                name: "Inventory & Stock",
                count: "11 articles",
                desc: "Real-time tracking, batch management",
              },
              {
                icon: (
                  <>
                    <rect x="1" y="4" width="22" height="16" rx="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </>
                ),
                color: "rgba(37,99,235,0.08)",
                stroke: "#2563eb",
                name: "Finance & Credit",
                count: "9 articles",
                desc: "Credit limits, DSO, payment terms",
              },
              {
                icon: (
                  <>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </>
                ),
                color: "rgba(51,65,85,0.07)",
                stroke: "#334155",
                name: "Distribution",
                count: "7 articles",
                desc: "Routing, van planning, outlet visits",
              },
            ].map((t) => (
              <div
                key={t.name}
                style={{
                  background: "#f8f6f2",
                  borderRadius: "14px",
                  border: "1px solid rgba(15,23,42,0.08)",
                  padding: "22px 20px",
                  cursor: "pointer",
                  transition: "box-shadow 0.18s,transform 0.18s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(15,23,42,0.09)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.boxShadow = "";
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: t.color,
                    marginBottom: "14px",
                  }}
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={t.stroke}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {t.icon}
                  </svg>
                </div>
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "var(--text-primary)",
                    margin: "0 0 5px",
                    lineHeight: "1.3",
                  }}
                >
                  {t.name}
                </h3>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    margin: "0 0 8px",
                  }}
                >
                  {t.desc}
                </p>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: t.stroke,
                    letterSpacing: "0.04em",
                  }}
                >
                  {t.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="cta-band blog-cta-band grain"
        aria-labelledby="blog-cta-title"
      >
        <div className="container">
          <div className="cta-band__content section-reveal">
            <span className="overline overline--light">GET STARTED</span>
            <h2 id="blog-cta-title">
              Ready to run a tighter <em>operation?</em>
            </h2>
            <p>
              Join the beverage businesses already streamlining orders,
              inventory, and payments with Maltora.
            </p>
            <div className="btn-row">
              <Link
                to="/contact"
                state={{ fromApp: true }}
                className="btn btn-accent btn-lg"
              >
                Get in touch
              </Link>
              <Link
                to="/pricing"
                state={{ fromApp: true }}
                className="btn btn-ghost-dark btn-lg"
              >
                See pricing →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
